import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {Asset} from '@app/@core/@models/advertising/asset.model';
import {FormArray, FormControl} from '@angular/forms';
import {AspectRatio, AssetComponent, DEFAULT_ASPECT_RATIOS, Dimensions} from '@app/@core/@models/advertising/layout/asset-component.model';
import {I18nFormatService} from '@app/@i18n/services/i18n-format.service';
import {AssetComponentForm} from '@app/@core/advertising/campaign/layout/asset-comoponent-form';
import {ValidationWarningMessage} from '@app/@core/@models/advertising/validation-warning-message.model';
import {MediaTypeHelper} from '@app/@core/design/media-type-helper.service';
import { Uuid } from '@shared/utils/uuid';
import { AdvertDraft } from '@app/@core/advertising/campaign/advert/advert-draft';
import { ConfigService } from '@app/@core/@config/config.service';
import { BestResults } from '@app/@core/@config/registered-urls-data';

@Component({
  selector: 'app-asset-layout-component[anon][creativeId][assetComponentForm][advertDraft][assets][serverValidationWarnings]',
  templateUrl: './asset-layout.component.html',
  styleUrls: ['./asset-layout.component.scss']
})
export class AssetLayoutComponent implements OnInit, OnChanges {
  @Input() anon!: boolean;
  @Input() creativeId!: string;
  @Input() assetComponentForm!: AssetComponentForm;
  @Input() assets!: FormArray<FormControl<Asset>>;
  @Input() advertDraft!: AdvertDraft;
  @Input() serverValidationWarnings?: ValidationWarningMessage[];
  aspectRatio: AspectRatio | undefined;

  constructor(public i18nFormat: I18nFormatService,
              private mediaTypeHelper: MediaTypeHelper) {
  }

  ngOnInit(): void {
    this.aspectRatio = this.assetComponentForm.value.layoutComponent?.aspectRatio;
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  fileUploaded(asset: Asset) {
    this.assetIdControl().setValue(asset.id);
    this.assetIdControl().markAsDirty();
    this.assets.push(new FormControl(asset, {nonNullable: true}));
    this.assets.markAsDirty();
  }

  getMediaTypeTranslationsCS(): string {
    const typeTranslations = [];
    for (let mediaType of this.mediaTypeHelper.getSupportedMediaTypes(this.assetComponent())) {
      const key = 'media.' + mediaType.id;
      const typeTranslation = this.i18nFormat.translate(key);
      if (key !== typeTranslation) {
        typeTranslations.push(typeTranslation);
      }
    }
    return typeTranslations.join(', ');
  }

  assetComponent(): AssetComponent {
    return this.assetComponentForm.value.layoutComponent!;
  }

  assetIdControl(): FormControl<string> {
    return this.assetComponentForm.controls.componentValue;
  }

  getAsset(): Asset | undefined {
    const assetId = this.assetIdControl().value;
    return this.assets.value.find(asset => asset.id === assetId);
  }

  isInvalid(): boolean {
    return this.assetIdControl().invalid || this.invalidSize() || this.invalidAspectRatio() || this.showServerValidationWarning();
  }

  showServerValidationWarning(): boolean {
    return this.assetIdControl().pristine && !!this.serverValidationWarnings?.length;
  }

  invalidSize(): boolean {
    const asset = this.getAsset();
    return !!asset && asset.fileSizeBytes > this.assetComponent().maxFileSizeBytes;
  }

  invalidAspectRatio(): boolean {
    const asset = this.getAsset();
    const ratioDimensions = this.getDimensionsFromAspectRatio();
    if (!ratioDimensions) {
      return false;
    }
    return !!asset && (asset.width / asset.height) !== (ratioDimensions.width / ratioDimensions.height);
  }

  getRequiredAspectRatio(): Dimensions {
    const ratioDimensions = this.getDimensionsFromAspectRatio();
    if (!ratioDimensions) {
      return {width: 1, height: 1};
    }
    return { width: ratioDimensions.width, height: ratioDimensions.height };
  }

  private getDimensionsFromAspectRatio(): Dimensions | null {
    const aspectRatio = this.assetComponent().aspectRatio;
    if (!aspectRatio) {
      return null;
    }
    return DEFAULT_ASPECT_RATIOS[aspectRatio] || null;
  }
}
